import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const terraCotta: TemplateRegistration = {
  meta: {
    id: 'terra-cotta',
    name: 'Terra Cotta',
    category: 'Creative',
    description: 'Earthy terracotta tones with organic blob decorations',
    previewImage: 'https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Fraunces', 'Public Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-light">
        <div class="container">
          <h2 class="section-title">About Us</h2>
          <div class="about-story">
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceRows = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-row">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <div class="service-text">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-alt">${serviceRows}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => {
        const blobClass = i % 3 === 0 ? 'blob-a' : i % 3 === 1 ? 'blob-b' : 'blob-c';
        return `<div class="gallery-item ${blobClass}"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-light">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map((t) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>${tContent}</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-list">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-light">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-light">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-centered">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #C2703E; --bg: #FFF9F5; --bg-alt: #FFF0E6; --accent: #87A878;
      --text: #3D2B1F; --text-muted: #7C6354;
      --font-heading: 'Fraunces', serif; --font-body: 'Public Sans', sans-serif;
      --shadow-sm: 0 2px 8px rgba(194,112,62,.08); --shadow-md: 0 6px 24px rgba(194,112,62,.12);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* BLOB DECORATION */
    .blob-section { position: relative; }
    .blob-section::before { content: ''; position: absolute; top: -40px; right: -60px; width: 200px; height: 200px; background: var(--accent); opacity: .08; border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; pointer-events: none; }
    .blob-section::after { content: ''; position: absolute; bottom: -30px; left: -40px; width: 150px; height: 150px; background: var(--primary); opacity: .06; border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%; pointer-events: none; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,249,245,.95); backdrop-filter: blur(10px); border-bottom: 1px solid #F5D6C3; box-shadow: var(--shadow-sm); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--primary); font-weight: 700; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
    .nav-links a:hover { color: var(--primary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* HERO - centered decorative, no image */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; background: var(--bg); }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 80px; max-width: 800px; margin: 0 auto; width: 100%; text-align: center; }
    .hero-ornament-top, .hero-ornament-bottom { display: block; color: var(--primary); font-size: 2rem; letter-spacing: 12px; opacity: .5; margin: 0 auto; }
    .hero-ornament-top { margin-bottom: 24px; }
    .hero-ornament-bottom { margin-top: 24px; margin-bottom: 32px; }
    .hero-divider { width: 120px; height: 3px; background: linear-gradient(90deg, transparent, var(--primary), transparent); margin: 0 auto; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.6rem, 7vw, 4.5rem); line-height: 1.15; margin-bottom: 16px; font-weight: 700; color: var(--text); }
    .hero h1 .accent { color: var(--primary); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 560px; margin: 0 auto 16px; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--primary); color: #fff; font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600; border: none; border-radius: 12px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--shadow-md); }
    .hero-cta:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(194,112,62,.25); opacity: 1; color: #fff; }

    /* SECTIONS */
    .section { padding: 80px 0; position: relative; overflow: hidden; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 700; margin-bottom: 40px; color: var(--text); }

    /* ABOUT - drop-cap story */
    .about-story { max-width: 780px; margin: 0 auto; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); max-width: 800px; line-height: 1.9; }
    .about-text::first-letter { font-family: var(--font-heading); font-size: 4rem; float: left; line-height: 1; margin-right: 12px; margin-top: 6px; color: var(--primary); font-weight: 700; }

    /* SERVICES - alternating rows */
    .services-alt { display: flex; flex-direction: column; gap: 0; }
    .service-row { display: flex; align-items: center; gap: 32px; padding: 36px 28px; border-bottom: 1px solid #F5D6C3; transition: background .3s; }
    .service-row:last-child { border-bottom: none; }
    .service-row:hover { background: rgba(194,112,62,.03); }
    .service-row:nth-child(even) { flex-direction: row-reverse; }
    .service-row .service-icon { flex-shrink: 0; }
    .icon-char { font-size: 28px; line-height: 1; color: var(--accent); }
    .service-row .service-text { flex: 1; }
    .service-row h3 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .service-row p { color: var(--text-muted); font-size: .92rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--primary); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; box-shadow: var(--shadow-sm); transition: transform .3s; }
    .blob-a img { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
    .blob-b img { border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%; }
    .blob-c img { border-radius: 50% 50% 30% 70% / 60% 40% 60% 40%; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS - blockquote with decorative quotation marks */
    .testimonials-list { display: flex; flex-direction: column; gap: 32px; max-width: 800px; margin: 0 auto; }
    .testimonial-card { position: relative; padding: 40px 40px 32px; background: #fff; border-radius: 16px; box-shadow: var(--shadow-sm); }
    .testimonial-card::before { content: '\\201C'; position: absolute; top: 12px; left: 20px; font-family: var(--font-heading); font-size: 4rem; color: var(--primary); opacity: .25; line-height: 1; }
    .testimonial-card::after { content: '\\201D'; position: absolute; bottom: 8px; right: 20px; font-family: var(--font-heading); font-size: 4rem; color: var(--primary); opacity: .25; line-height: 1; }
    .testimonial-body p { font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #E5D5CA; font-size: 1rem; }
    .star.filled { color: var(--primary); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 1px solid #F5D6C3; padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 10px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid #F5D6C3; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--primary); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #F5D6C3; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #fff; padding: 20px 0; border-bottom: 1px solid #F5D6C3; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: #fff; font-family: var(--font-heading); padding: 8px 22px; font-size: 1rem; font-weight: 700; border-radius: 8px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - centered card */
    .contact-centered { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 16px; padding: 48px 40px; box-shadow: var(--shadow-md); }
    .contact-info { display: flex; flex-direction: column; gap: 16px; margin-bottom: 28px; text-align: center; align-items: center; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; color: var(--accent); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 1px solid #F5D6C3; border-radius: 12px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); }
    .contact-form button { padding: 14px; background: var(--primary); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; border: none; border-radius: 12px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(194,112,62,.25); }

    /* FOOTER */
    .footer { background: #3D2B1F; padding: 40px 0; text-align: center; }
    .footer p { color: #C4A98C; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,249,245,.98); border-bottom: 1px solid #F5D6C3; padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .service-row, .service-row:nth-child(even) { flex-direction: column; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-centered { padding: 32px 20px; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-content">
      <div class="hero-ornament-top">&#10053; &#10053; &#10053;</div>
      <div class="hero-divider"></div>
      <h1><span class="accent">${safeHeadline}</span></h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      <div class="hero-divider"></div>
      <div class="hero-ornament-bottom">&#10053; &#10053; &#10053;</div>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
